import * as React from "react";
import * as moment from "moment";
import ButtonPrimary from "../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import {evColors} from "./evChargingStyles";

/**
 * A file chosen as evidence for the job, kept in the browser until the job is saved.
 */
export interface EvidenceFile {
	key: string;
	file: File;
	/**
	 * Object URL of the file, made by the page so it can be released when the file goes.
	 */
	url: string;
	/**
	 * Set once uploaded, so a refused save that is retried does not upload it again.
	 */
	attachmentId?: string;
}

interface Props {
	files: EvidenceFile[];
	selectedKey?: string;
	onFilesAdded: (files: File[]) => void;
	onFileSelected: (key: string) => void;
	onFileRemoved: (key: string) => void;
}

interface State {
	/**
	 * Files are being dragged over the panel.
	 */
	dragging: boolean;
}

/**
 * The evidence of the energy claimed, open beside the form so its numbers are copied without switching windows: the
 * bill, or an export of charging sessions with screenshots of the charger's portal. Files are chosen, dropped on the
 * panel or pasted anywhere on the page, which is how a screenshot arrives straight from the clipboard. Any file type
 * is taken; PDFs and images show here, others only by name.
 */
export default class BillPreview extends React.Component<Props, State> {
	state: State = {dragging: false};

	private input: HTMLInputElement | null = null;

	/**
	 * dragenter and dragleave fire for every child crossed, so the panel counts them to know when the drag has left.
	 */
	private dragDepth = 0;

	componentDidMount() {
		window.addEventListener("paste", this.onPaste);
		window.addEventListener("dragover", ignoreMissedDrop);
		window.addEventListener("drop", ignoreMissedDrop);
	}

	componentWillUnmount() {
		window.removeEventListener("paste", this.onPaste);
		window.removeEventListener("dragover", ignoreMissedDrop);
		window.removeEventListener("drop", ignoreMissedDrop);
	}

	render() {
		const {files, selectedKey} = this.props;
		const selected = files.filter(f => f.key === selectedKey)[0];
		return (
			<div aria-label="Evidence" onDragEnter={this.onDragEnter} onDragOver={this.onDragOver}
				 onDragLeave={this.onDragLeave} onDrop={this.onDrop}>
				<div style={styles.header}>
					<div style={styles.title}>Evidence</div>
					<ButtonPrimary label={"Add files"} variant={"lighter-blue"} onClick={this.openChooser}/>
					<input ref={el => this.input = el} type="file" multiple style={{display: "none"}}
						   onChange={this.onChange}/>
				</div>
				{files.length > 0 &&
				<ul style={styles.list} aria-label={"Files"}>
					{files.map(f => this.renderChip(f, f.key === selectedKey))}
				</ul>}
				<div style={{...styles.body, ...(this.state.dragging ? styles.bodyDragging : {})}}>
					{this.state.dragging
						? <div style={styles.dropHint}>Drop to add</div>
						: selected ? this.renderFile(selected) : this.renderEmpty()}
				</div>
			</div>
		);
	}

	private renderChip(evidence: EvidenceFile, selected: boolean) {
		const name = evidence.file.name;
		return (
			<li key={evidence.key} style={{...styles.chip, ...(selected ? styles.chipSelected : {})}}>
				<button type="button" style={{...styles.chipName, color: selected ? "white" : evColors.navy}}
						aria-pressed={selected} title={name} onClick={() => this.props.onFileSelected(evidence.key)}>
					{name}
				</button>
				<button type="button" style={{...styles.remove, color: selected ? "white" : evColors.muted}}
						aria-label={`Remove ${name}`} onClick={() => this.props.onFileRemoved(evidence.key)}>
					&#10005;
				</button>
			</li>
		);
	}

	private renderFile(evidence: EvidenceFile) {
		const {file, url} = evidence;
		if (file.type === "application/pdf")
			// navpanes=0 keeps the browser's page thumbnails closed, which otherwise cover half the bill
			return <object data={`${url}#navpanes=0&view=FitH`} type="application/pdf" style={styles.document}
						   aria-label={file.name}/>;
		if (file.type.indexOf("image/") === 0)
			return <img src={url} alt={file.name} style={styles.image}/>;
		return (
			<div style={styles.noPreview}>
				<div>No preview</div>
				<a href={url} download={file.name} className={"t-link"}>{file.name}</a>
			</div>
		);
	}

	private renderEmpty() {
		return (
			<button type="button" onClick={this.openChooser} style={styles.empty}>
				Add files, drop them here or paste a screenshot
			</button>
		);
	}

	private openChooser = () => this.input && this.input.click();

	private onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.add(toArray(event.target.files));
		// lets the same file be chosen again after it was removed
		event.target.value = "";
	};

	private onDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
		if (!carriesFiles(event.dataTransfer)) return;
		event.preventDefault();
		if (this.dragDepth++ === 0) this.setState({dragging: true});
	};

	private onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
		// without it the browser opens the dropped file in place of the page
		if (carriesFiles(event.dataTransfer)) event.preventDefault();
	};

	private onDragLeave = () => {
		if (this.dragDepth > 0 && --this.dragDepth === 0) this.setState({dragging: false});
	};

	private onDrop = (event: React.DragEvent<HTMLDivElement>) => {
		if (!carriesFiles(event.dataTransfer)) return;
		event.preventDefault();
		this.dragDepth = 0;
		this.setState({dragging: false});
		this.add(toArray(event.dataTransfer.files));
	};

	/**
	 * Takes files from the clipboard wherever the page has focus. Text pasted into a field carries no files and is
	 * left to the field.
	 */
	private onPaste = (event: ClipboardEvent) => {
		const files = toArray(event.clipboardData && event.clipboardData.files);
		if (!files.length) return;
		event.preventDefault();
		this.add(files.map(namePasted));
	};

	private add(files: File[]) {
		if (files.length) this.props.onFilesAdded(files);
	}
}

function toArray(list: FileList | null | undefined): File[] {
	const files: File[] = [];
	if (list) for (let i = 0; i < list.length; i++) files.push(list[i]);
	return files;
}

/**
 * A file dropped beside the panel would otherwise be opened by the browser in place of the page, losing the form.
 */
function ignoreMissedDrop(event: DragEvent) {
	if (event.dataTransfer && carriesFiles(event.dataTransfer)) event.preventDefault();
}

function carriesFiles(transfer: DataTransfer): boolean {
	const types = transfer && transfer.types;
	if (!types) return false;
	for (let i = 0; i < types.length; i++)
		if (types[i] === "Files") return true;
	return false;
}

/**
 * A screenshot pasted from the clipboard is called "image.png" whatever it shows, so each one is named by the time it
 * was pasted, which keeps several apart on the job.
 */
function namePasted(file: File, index: number): File {
	if (file.name && file.name !== "image.png") return file;
	const extension = file.type.split("/")[1] || "png";
	const suffix = index > 0 ? `-${index + 1}` : "";
	return new File([file], `screenshot-${moment().format("YYYY-MM-DD-HHmmss")}${suffix}.${extension}`, {type: file.type});
}

const styles: { [key: string]: React.CSSProperties } = {
	header: {display: "flex", alignItems: "center", gap: 12, marginBottom: 12},
	title: {flexGrow: 1, fontWeight: 600},
	list: {listStyle: "none", margin: "0 0 12px", padding: 0, display: "flex", flexWrap: "wrap", gap: 8},
	chip: {
		display: "flex", alignItems: "center", maxWidth: "100%", border: `1px solid ${evColors.border}`, borderRadius: 4,
		background: "white"
	},
	// filled, so the file in the preview reads at a glance among the others
	chipSelected: {background: evColors.navy, borderColor: evColors.navy},
	chipName: {
		border: 0, background: "transparent", padding: "5px 4px 5px 10px", fontSize: 13, cursor: "pointer",
		overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260
	},
	remove: {border: 0, background: "transparent", padding: "5px 9px", cursor: "pointer"},
	body: {
		height: "calc(100vh - 330px)", minHeight: 520, background: evColors.background, borderRadius: 4,
		border: "2px solid transparent", display: "flex", alignItems: "center", justifyContent: "center"
	},
	bodyDragging: {border: `2px dashed ${evColors.navy}`},
	dropHint: {color: evColors.navy, fontSize: 15, fontWeight: 600},
	document: {width: "100%", height: "100%", border: 0, borderRadius: 4},
	image: {maxWidth: "100%", maxHeight: "100%", objectFit: "contain"},
	noPreview: {display: "flex", flexDirection: "column", alignItems: "center", gap: 8, color: evColors.muted, fontSize: 14},
	empty: {
		width: "calc(100% - 32px)", height: "calc(100% - 32px)", border: `2px dashed ${evColors.border}`, borderRadius: 4,
		background: "transparent", color: evColors.muted, fontSize: 14, cursor: "pointer"
	}
};
